import React from 'react'
import { Rule, Tier } from 'types/progressiveDiscountTypes'
import {
  formatBRL,
  formatTierFloor,
  itemBenefitLabel,
  nextTierHint,
  NextTierHint,
  pureItemValueLabel,
  shippingBenefitLabel,
} from 'utils/progressiveDiscount'
import * as Style from './styles'

interface ActiveTier {
  min_quantity: number
  discount_percent: number
}

function buildHintText(hint: NextTierHint | null): string {
  if (!hint) return 'Faixa máxima atingida!'

  const floor = hint.isAmountTrigger ? hint.missingLabel : `${hint.missing} un`

  if (hint.itemLabel) {
    return `Faltam ${floor} para ${hint.itemLabel}${hint.freightLabel ? ` + ${hint.freightLabel}` : ''}`
  }

  if (hint.pureValueLabel) {
    return `Faltam ${floor} para ${hint.pureValueLabel}`
  }

  return `Faltam ${floor} para ${hint.nextPercent}% OFF`
}

// Texto do "% OFF" no topo do card — 'both' junta item+frete; 'shipping' puro usa shippingBenefitLabel
// (item nao muda de preco); item puro respeita discount_type.
function buildBadgeText(activeTier: ActiveTier, fullActiveTier: Tier | undefined, rule: Rule | undefined, unitPrice: number | undefined): string {
  if (rule?.benefit_target === 'both' && fullActiveTier) {
    const item = itemBenefitLabel(fullActiveTier, rule.discount_type, unitPrice)
    const freight = shippingBenefitLabel(fullActiveTier)
    return freight ? `${item} + ${freight}` : item
  }

  if (rule?.benefit_target === 'shipping' && fullActiveTier) {
    return shippingBenefitLabel(fullActiveTier) ?? `${activeTier.discount_percent}% OFF`
  }

  if (rule?.discount_type === 'value' && fullActiveTier) {
    return pureItemValueLabel(fullActiveTier, unitPrice)
  }

  return `${activeTier.discount_percent}% OFF`
}

function buildMilestoneLabel(tier: Tier, rule: Rule | undefined, unitPrice: number | undefined): string {
  const floor = formatTierFloor(tier, rule?.trigger_type)

  if (rule?.benefit_target === 'both') {
    const item = itemBenefitLabel(tier, rule.discount_type, unitPrice)
    const freight = shippingBenefitLabel(tier)
    return `${floor} · ${freight ? `${item} + ${freight}` : item}`
  }

  if (rule?.benefit_target === 'shipping') {
    return `${floor} · ${shippingBenefitLabel(tier) ?? `${tier.discount_percent}%`}`
  }

  if (rule?.discount_type === 'value') {
    return `${floor} · ${pureItemValueLabel(tier, unitPrice)}`
  }

  return `${floor} · ${tier.discount_percent}%`
}

interface Props {
  ruleName: string
  groupQty: number
  activeTier: ActiveTier
  rule?: Rule
  unitPrice?: number
  tiers: Tier[]
  savings: number
}

export const CartProgressiveGroupCard = ({ ruleName, groupQty, activeTier, rule, unitPrice, tiers, savings }: Props) => {
  const sorted = [...tiers].sort((a, b) => a.min_quantity - b.min_quantity)
  const maxMin = sorted.length > 0 ? sorted[sorted.length - 1].min_quantity : 1
  const progressPct = maxMin > 0 ? (Math.min(groupQty, maxMin) / maxMin) * 100 : 0
  const fullActiveTier = sorted.find(t => t.min_quantity === activeTier.min_quantity)

  const milestones = sorted.map(t => ({
    label: buildMilestoneLabel(t, rule, unitPrice),
    reached: groupQty >= t.min_quantity,
  }))

  const hint = nextTierHint(sorted, groupQty, rule, unitPrice)
  const hintText = buildHintText(hint)

  return (
    <Style.GroupCard>
      <Style.GroupHeader>
        <Style.GroupName>{ruleName}</Style.GroupName>
        <Style.OffBadge>{buildBadgeText(activeTier, fullActiveTier, rule, unitPrice)}</Style.OffBadge>
      </Style.GroupHeader>

      <Style.ProgressSection>
        <Style.ProgressInfo>
          <Style.ProgressQtyLabel>Total do grupo: {groupQty} un</Style.ProgressQtyLabel>
          <Style.ProgressHint>{hintText}</Style.ProgressHint>
        </Style.ProgressInfo>
        <Style.ProgressBarWrap>
          <Style.ProgressBarFill pct={progressPct} />
        </Style.ProgressBarWrap>
        <Style.Milestones>
          {milestones.map(m => (
            <Style.MilestoneItem key={m.label} reached={m.reached}>
              {m.reached ? '●' : '○'} {m.label}
            </Style.MilestoneItem>
          ))}
        </Style.Milestones>
      </Style.ProgressSection>

      {savings > 0 && (
        <Style.GroupFooter>
          <Style.FooterSavings>economia: {formatBRL(savings)}</Style.FooterSavings>
        </Style.GroupFooter>
      )}
    </Style.GroupCard>
  )
}
